
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import MarketplaceForm from './MarketplaceForm';
import MarketplaceTable, { MarketplaceListing } from './MarketplaceTable';

interface MarketplaceSectionProps {
  isAdmin: boolean;
}

const MarketplaceSection = ({ isAdmin }: MarketplaceSectionProps) => {
  const [listings, setListings] = useState<MarketplaceListing[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchListings();
  }, []);
  
  const fetchListings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('community_marketplace')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setListings(data || []);
    } catch (error) {
      console.error('Error fetching marketplace listings:', error);
      toast.error('Failed to load marketplace listings');
    } finally {
      setLoading(false);
    }
  };
  
  const toggleApproval = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('community_marketplace')
        .update({ approved: !currentStatus })
        .eq('id', id);
      
      if (error) throw error;
      
      toast.success(`Listing ${!currentStatus ? 'approved' : 'unapproved'}`);
      fetchListings();
    } catch (error) {
      console.error('Error updating approval status:', error);
      toast.error("Failed to update approval status");
    }
  };
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Community Marketplace</CardTitle>
              <CardDescription>Discover local businesses and services</CardDescription>
            </div>
            <Button onClick={() => setShowForm(true)}>
              Add Business
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <MarketplaceForm 
            open={showForm}
            onOpenChange={setShowForm}
            onBusinessAdded={fetchListings}
          />
          
          <MarketplaceTable
            listings={listings}
            loading={loading}
            isAdmin={isAdmin}
            onToggleApproval={toggleApproval}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default MarketplaceSection;
